// src/components/users/AddUserModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import api from '../../../utils/api';
import { UserInput,UserRole } from '../../../models/User';
import { ActionButton,CancelButton } from '../common/AdminButtons';
import {Form,FormGroup,ButtonGroup,Input, Select, Label} from '../common/AdminForm';
import { LoadingMessage, ErrorMessage } from '../common/AdminMessages';

const Dialog = styled.dialog`
  padding: 2rem;
  border: none;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 90%;

  &::backdrop {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

interface AddUserFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const AddUserForm: React.FC<AddUserFormProps> = ({ onSuccess,onCancel }) => {
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [formData, setFormData] = useState<UserInput>({
    name: '',
    email: '',
    role_id: 0
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await api.get<UserRole[]>('/halo/users/roles');
        setRoles(response.data);
        if (response.data.length > 0) {
          setFormData(prev => ({ ...prev, role_id: response.data[0].id }));
        }
      } catch (error) {
        console.error('Error fetching roles:', error);
      }
    };

    fetchRoles();
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!formData.email.endsWith('@halo22.net')) {
      newErrors.email = 'Email must end with @halo22.net';
    }
    
    if (!formData.role_id) {
      newErrors.role_id = 'Role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      await api.post('/halo/users', formData);
      onSuccess();
      dialogRef.current?.close();
      setFormData({ name: '', email: '', role_id: roles[0]?.id || 0 });
    } catch (error) {
      console.error('Error creating user:', error);
      setErrors(prev => ({ ...prev, submit: 'Failed to create user. Please try again.' }));
    }
  };

  return (
      <Form onSubmit={handleSubmit}>
        <h2>Add New User</h2>
        
        <FormGroup>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            type="text"
            value={formData.name}
            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
          />
          {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
          />
          {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="role">Role</Label>
          <Select
            id="role"
            value={formData.role_id}
            onChange={e => setFormData(prev => ({ ...prev, role_id: Number(e.target.value) }))}
          >
            {roles.map(role => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </Select>
          {errors.role_id && <ErrorMessage>{errors.role_id}</ErrorMessage>}
        </FormGroup>

        {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}

        <ButtonGroup>
          
          <ActionButton type="submit">
            Add User
          </ActionButton>
          <CancelButton 
            type="button" 
            onClick={onCancel}
          >
            Cancel
          </CancelButton>
        </ButtonGroup>
      </Form>
  );
};